import {
  calculateOverallWinnerProbabilities,
  calculateExtraTransactions,
} from './statisticsUtils';

/** Sum sessions across control and all variants */
export const getTotalUsers = (analysis) => {
  if (!analysis?.control) return '0';

  let total = analysis.control.sessions || 0;

  if (analysis.variants && Array.isArray(analysis.variants)) {
    analysis.variants.forEach((variant) => {
      if (variant) total += variant.sessions || 0;
    });
  } else if (analysis.variant) {
    total += analysis.variant.sessions || 0;
  }

  return total.toLocaleString();
};

/** Win probability per variant vs control (binary comparisons) */
export const calculateBinaryWinnerProbabilities = (analysis) => {
  if (!analysis?.control) return null;

  try {
    const results = [];
    const controlData = {
      name: analysis.control.name || 'Control',
      conversions: analysis.control.conversions || 0,
      sessions: analysis.control.sessions || 0,
      isControl: true,
    };

    const variants = [];
    if (analysis.variants && Array.isArray(analysis.variants)) {
      analysis.variants.forEach((variant, index) => {
        if (variant) {
          variants.push({
            name: variant.name || `Varyasyon ${index + 1}`,
            conversions: variant.conversions || 0,
            sessions: variant.sessions || 0,
            isControl: false,
          });
        }
      });
    } else if (analysis.variant) {
      variants.push({
        name: analysis.variant.name || 'Varyasyon 1',
        conversions: analysis.variant.conversions || 0,
        sessions: analysis.variant.sessions || 0,
        isControl: false,
      });
    }

    variants.forEach((variant) => {
      const binaryComparisonData = [controlData, variant];
      const binaryWinnerProbs = calculateOverallWinnerProbabilities(binaryComparisonData);

      if (binaryWinnerProbs?.length === 2) {
        const controlProb = binaryWinnerProbs.find((p) => p.isControl);
        const variantProb = binaryWinnerProbs.find((p) => !p.isControl);
        results.push({
          variantName: variant.name,
          controlWinProbability: controlProb ? controlProb.winProbability : 0,
          variantWinProbability: variantProb ? variantProb.winProbability : 0,
        });
      }
    });

    return results;
  } catch (error) {
    console.error('Error calculating binary winner probabilities:', error);
    return null;
  }
};

/** Relative uplift between variant and control CR */
export const calculateUplift = (variantCR, controlCR) => {
  if (!controlCR) return 0;
  return ((variantCR - controlCR) / controlCR) * 100;
};

/** Monthly/yearly extra transactions for a variant */
export const calculateVariantExtraTransactions = (variantData, controlData, testDuration) => {
  try {
    const dailyTraffic = 1000;
    const trafficSplit = 50;

    return calculateExtraTransactions(
      controlData.conversions,
      controlData.sessions,
      variantData.conversions,
      variantData.sessions,
      dailyTraffic,
      trafficSplit / 100,
      null,
      testDuration,
    );
  } catch (error) {
    console.error('Error calculating extra transactions:', error);
    return null;
  }
};

/** Slice report data for the active metric tab */
export const buildSelectedMetricData = (originalData, metricIndex = 0) => {
  const analysis = originalData.analysis?.[metricIndex];
  if (!analysis) return null;

  return {
    ...originalData,
    analysis,
    resultStatus: analysis.resultStatus,
    sessionTab: analysis.control?.tabName || 'Sessions',
    conversionTab: analysis.name || 'Transactions',
  };
};

/** Map report payload to AB Test Calculator import shape */
export const convertReportToCalculatorFormat = (reportData) => {
  const variants = [];

  variants.push({
    name: reportData.analysis.control.name || 'Control',
    sessions: reportData.analysis.control.sessions.toString(),
    conversions: reportData.analysis.control.conversions.toString(),
    isControl: true,
  });

  if (reportData.analysis.variants && Array.isArray(reportData.analysis.variants)) {
    reportData.analysis.variants.forEach((variant, index) => {
      if (variant) {
        variants.push({
          name: variant.name || `Variant ${String.fromCharCode(65 + index)}`,
          sessions: (variant.sessions || 0).toString(),
          conversions: (variant.conversions || 0).toString(),
          isControl: false,
        });
      }
    });
  } else if (reportData.analysis.variant) {
    variants.push({
      name: reportData.analysis.variant.name || 'Variant A',
      sessions: (reportData.analysis.variant.sessions || 0).toString(),
      conversions: (reportData.analysis.variant.conversions || 0).toString(),
      isControl: false,
    });
  }

  return {
    variants,
    postTestSettings: {
      analysisMethod: 'bayesian',
      confidenceLevel: 0.95,
      testDuration: reportData.testDuration || 14,
      dailyTraffic: 1000,
      trafficSplit: 50,
      averageOrderValue: reportData.averageOrderValue || 0,
    },
    sourceReportId: reportData._id || null,
  };
};

/** Resolve brand display name from API payload */
export const getReportBrandName = (report) => {
  if (!report) return 'Diğer';
  if (typeof report.brand === 'object') return report.brand?.name || 'Diğer';
  if (typeof report.brandName === 'string') return report.brandName;
  return report.brand || 'Diğer';
};
